import { Search } from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';
import {
  Input,
  TableBody,
  TableFooterStyle4,
  TableHeader,
} from 'vet-bones/design-system/components';
import { alphaSort, numericSort } from 'vet-bones/utils';

import { DataTableHeaderCell } from 'src/ui/components/DataTableHeaderCell';

export interface DataTableColumn<T> {
  key: string;
  className?: string;
  header: string;
  sortByAlpha?: (item: T) => string;
  sortByNumeric?: (item: T) => number;
  sortTestId?: string;
  testId?: string;
}

export interface DataTableProps<T> {
  className?: string;
  columns: Array<DataTableColumn<T>>;
  data: T[];
  getRowKey: (item: T, index: number) => string;
  initialSortIdx?: number;
  initialSortAsc?: boolean;
  pageSize?: number;
  pageTestId?: (page: string | number) => string;
  preRow?: JSX.Element;
  renderRow: (item: T, index: number) => React.ReactNode;
  searchActions?: JSX.Element;
  searchFilter?: (item: T, query: string) => boolean;
  searchPlaceholder?: string;
  searchClearTestId?: string;
  searchInputTestId?: string;
  title: string;
}

export const DataTable = <T,>({
  className,
  columns,
  data,
  getRowKey,
  initialSortIdx = 0,
  initialSortAsc = true,
  pageSize = 10,
  pageTestId,
  preRow,
  renderRow,
  searchActions,
  searchFilter,
  searchPlaceholder,
  searchClearTestId,
  searchInputTestId,
  title,
}: DataTableProps<T>): React.ReactElement => {
  const [sortIdx, setSortIdx] = React.useState(initialSortIdx);
  const [sortAsc, setSortAsc] = React.useState(initialSortAsc);

  const [searchQuery, setSearchQuery] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);

  const filteredData = React.useMemo(() => {
    if (!searchQuery || !searchFilter) {
      return data;
    }
    return data.filter((item) => searchFilter(item, searchQuery));
  }, [data, searchQuery, searchFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));

  const paginatedData = React.useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const sortedData = [...filteredData];
    if (sortIdx !== null) {
      if (columns[sortIdx]?.sortByAlpha) {
        sortedData.sort(alphaSort(columns[sortIdx].sortByAlpha, !sortAsc));
      }
      if (columns[sortIdx]?.sortByNumeric) {
        sortedData.sort(numericSort(columns[sortIdx].sortByNumeric, !sortAsc));
      }
    }
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [currentPage, pageSize, filteredData, sortIdx, sortAsc, columns]);

  React.useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
    if (sortIdx > columns.length - 1) {
      setSortIdx(initialSortIdx);
      setSortAsc(initialSortAsc);
    }
  }, [
    columns,
    currentPage,
    initialSortAsc,
    initialSortIdx,
    sortIdx,
    totalPages,
  ]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const tableHeader = (
    <>
      {columns.map((column, colIdx) => (
        <DataTableHeaderCell
          // eslint-disable-next-line react/no-array-index-key
          key={colIdx}
          colIdx={colIdx}
          column={column}
          onSort={() => {
            setSortIdx(colIdx);
            setSortAsc(sortIdx === colIdx ? !sortAsc : initialSortAsc);
          }}
          sortAsc={sortAsc}
          sortIdx={sortIdx}
        />
      ))}
    </>
  );

  const tableBody = paginatedData.map((item, index) => (
    <React.Fragment key={getRowKey(item, index)}>
      {renderRow(item, index)}
    </React.Fragment>
  ));

  if (preRow) {
    tableBody.unshift(<>{preRow}</>);
  }

  return (
    <div className={classNames('vb-data-table', 'data-table', className)}>
      <TableHeader
        actions={
          <div className="data-table-actions">
            {searchFilter ? (
              <Input
                clearable
                clearTestId={searchClearTestId}
                icon={Search}
                label=""
                onChange={handleSearchChange}
                onClear={() => {
                  setSearchQuery('');
                  setCurrentPage(1);
                }}
                placeholder={searchPlaceholder}
                size="md"
                testId={searchInputTestId}
                value={searchQuery}
              />
            ) : null}
            {searchActions}
          </div>
        }
        title={title}
      />
      <TableBody body={tableBody} header={tableHeader} size="sm" />
      {totalPages > 1 ? (
        <TableFooterStyle4
          onPage={handlePageChange}
          page={currentPage}
          pages={totalPages}
          pageTestId={pageTestId}
          size="md"
        />
      ) : null}
    </div>
  );
};
